/** Stored values remain Arabic strings for API/DB compatibility; UI can show EN via label helpers. */

export const SHAREHOLDER_TYPE_OPTIONS = [
  { value: 'مساهم مؤسس',   en: 'Founding shareholder', ur: 'بانی حصہ دار',       fr: 'Actionnaire fondateur',    zh: '创始股东' },
  { value: 'مساهم',         en: 'Shareholder',          ur: 'حصہ دار',            fr: 'Actionnaire',             zh: '普通股东' },
  { value: 'مساهم مستثمر', en: 'Investor shareholder', ur: 'سرمایہ کار حصہ دار', fr: 'Actionnaire investisseur', zh: '投资股东' },
];

export const SHARE_STOCK_TYPE_OPTIONS = [
  { value: 'سهم عادي',          en: 'Ordinary share',  ur: 'عام سہام',          fr: 'Action ordinaire',     zh: '普通股' },
  { value: 'سهم ممتاز',         en: 'Preferred share', ur: 'ممتاز سہام',        fr: 'Action privilégiée',   zh: '优先股' },
  { value: 'مخصصة للعاملين',    en: 'Employee shares', ur: 'ملازمین کے سہام',   fr: 'Actions pour employés', zh: '员工股' },
];

export const BANK_OPTIONS = [
  { ar: 'البنك الأهلي السعودي', en: 'Saudi National Bank (SNB)' },
  { ar: 'بنك الراجحي', en: 'Al Rajhi Bank' },
  { ar: 'بنك الرياض', en: 'Riyad Bank' },
  { ar: 'البنك السعودي الفرنسي', en: 'Banque Saudi Fransi (BSF)' },
  { ar: 'البنك العربي الوطني', en: 'Arab National Bank (ANB)' },
  { ar: 'بنك البلاد', en: 'Bank Albilad' },
  { ar: 'مصرف الإنماء', en: 'Alinma Bank' },
  { ar: 'البنك السعودي للاستثمار', en: 'The Saudi Investment Bank (SAIB)' },
  { ar: 'بنك الجزيرة', en: 'Bank AlJazira' },
  { ar: 'البنك الأول', en: 'SAB' },
  { ar: 'بنك الخليج الدولي', en: 'Gulf International Bank (GIB)' },
];

export const SHAREHOLDER_TYPES = SHAREHOLDER_TYPE_OPTIONS.map((o) => o.value);
export const SHARE_STOCK_TYPES = SHARE_STOCK_TYPE_OPTIONS.map((o) => o.value);

export const EMPTY_SHAREHOLDER_FORM = {
  name: '',
  branchId: '',
  ownership: '',
  type: SHAREHOLDER_TYPES[1],
  shareStockType: SHARE_STOCK_TYPES[0],
  phone: '',
  email: '',
  bankName: '',
  bankAccountNumber: '',
  iban: '',
  userName: '',
  password: '',
  status: 'active',
};

const SAMPLE_SHAREHOLDERS = [
  { name: 'عبدالرحمن صالح الغامدي', userBase: 'abdulrahman.alghamdi' },
  { name: 'هند ماجد العتيبي', userBase: 'hind.majed' },
  { name: 'ماجد سعود المطيري', userBase: 'majed.almutairi' },
  { name: 'لين علي العنزي', userBase: 'lin.ali' },
  { name: 'فيصل بندر الرشيد', userBase: 'faisal.b.alrasheed' },
  { name: 'أمل خالد القحطاني', userBase: 'amal.k.alqahtani' },
];

/**
 * Realistic sample for add-shareholder. Login + IBAN get a short unique part so repeated saves work.
 * @param {string} [branchId] – selected company id
 */
export function getSampleShareholderForm(branchId) {
  const n = Date.now();
  const tail = String(n).slice(-5);
  const i = n % SAMPLE_SHAREHOLDERS.length;
  const { name, userBase } = SAMPLE_SHAREHOLDERS[i];
  const mobile8 = String(10000000 + (n % 90000000)).padStart(8, '0').slice(0, 8);
  const pct = Math.min(45.9, Math.max(1.1, 6 + (n % 40) + (n % 7) / 10));
  const typePick = [SHAREHOLDER_TYPES[0], SHAREHOLDER_TYPES[1], SHAREHOLDER_TYPES[2]];
  const stockPick = [SHARE_STOCK_TYPES[0], SHARE_STOCK_TYPES[1]];
  const bank = BANK_OPTIONS[n % BANK_OPTIONS.length];
  const acct = String(1000000000000 + (n % 8000000000000)).padStart(13, '0').slice(0, 14);
  const ibanSeed = `${n}${(n * 17) % 1000000000000}${(n * 7919) % 10000000000000}`.replace(/\D/g, '');
  const iban22 = ibanSeed.padStart(22, '0').slice(0, 22);
  return {
    ...EMPTY_SHAREHOLDER_FORM,
    name,
    branchId: branchId != null && String(branchId) !== '' ? String(branchId) : '',
    ownership: pct.toFixed(1),
    type: typePick[n % typePick.length],
    shareStockType: stockPick[n % stockPick.length],
    phone: `05${mobile8}`,
    email: `${userBase}.${tail}@mithaq.sa`,
    bankName: bank.ar,
    bankAccountNumber: acct,
    iban: `SA${iban22}`,
    userName: `${userBase}.${tail}`,
    password: 'CorpPass@2024',
    status: 'active',
  };
}

export function normalizeShareholderType(type) {
  if (!type) return SHAREHOLDER_TYPES[1];
  if (SHAREHOLDER_TYPES.includes(type)) return type;
  const legacyMap = {
    مؤسسي: 'مساهم مؤسس',
    مستثمر: 'مساهم مستثمر',
    جمهور: 'مساهم',
  };
  return legacyMap[type] ?? SHAREHOLDER_TYPES[1];
}

export function normalizeShareStockType(type) {
  if (!type) return SHARE_STOCK_TYPES[0];
  if (SHARE_STOCK_TYPES.includes(type)) return type;
  return SHARE_STOCK_TYPES[0];
}

const DEFAULT_MSG_AR = {
  nameRequired: 'الاسم مطلوب',
  branchRequired: 'الشركة مطلوبة',
  ownershipRequired: 'نسبة الملكية مطلوبة',
  ownershipRange: 'نسبة الملكية يجب أن تكون بين 0.1 و 100',
  emailInvalid: 'البريد الإلكتروني غير صحيح',
  userNameRequired: 'اسم المستخدم مطلوب',
  passwordRequired: 'كلمة المرور مطلوبة',
  passwordMin: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
};

/** @param {Record<string, string>} [messages] localized validation messages */
export function validateShareholderForm(form, isNew = true, messages = {}) {
  const m = { ...DEFAULT_MSG_AR, ...messages };
  const errors = {};
  if (!form.name.trim()) errors.name = m.nameRequired;
  if (!form.branchId) errors.branchId = m.branchRequired;
  if (!form.ownership) errors.ownership = m.ownershipRequired;
  else if (Number.isNaN(Number(form.ownership)) || Number(form.ownership) <= 0 || Number(form.ownership) > 100) {
    errors.ownership = m.ownershipRange;
  }
  if (form.email && !/\S+@\S+\.\S+/.test(form.email)) errors.email = m.emailInvalid;
  if (isNew && !form.userName?.trim()) errors.userName = m.userNameRequired;
  if (isNew && !form.password?.trim()) errors.password = m.passwordRequired;
  else if (form.password && form.password.trim().length < 6) errors.password = m.passwordMin;
  return errors;
}

function _shLabel(opt, lang) {
  if (lang === 'ar') return opt.value;
  if (lang === 'ur') return opt.ur || opt.en;
  if (lang === 'fr') return opt.fr || opt.en;
  if (lang === 'zh') return opt.zh || opt.en;
  return opt.en;
}

/** @param {'ar'|'en'|'ur'|'fr'|'zh'} lang */
export function shareholderTypeLabel(storedValue, lang = 'ar') {
  if (!storedValue) return '';
  const opt = SHAREHOLDER_TYPE_OPTIONS.find((o) => o.value === storedValue);
  if (!opt) return storedValue;
  return _shLabel(opt, lang);
}

/** @param {'ar'|'en'|'ur'|'fr'|'zh'} lang */
export function shareStockTypeLabel(storedValue, lang = 'ar') {
  if (!storedValue) return '';
  const opt = SHARE_STOCK_TYPE_OPTIONS.find((o) => o.value === storedValue);
  if (!opt) return storedValue;
  return _shLabel(opt, lang);
}
